import {
  FuelTypeEnumSchema,
  HybridTypeEnumSchema,
  VehicleTypeEnum,
  VehicleTypeEnumSchema,
} from '@dms/api/vehicle';

import {FieldAvailabilityContext} from './mandatoryFieldsMap';

/**
 * Subset of vehicle data shape needed to derive a {@link FieldAvailabilityContext}.
 *
 * Compatible with both response shapes used across the app:
 *  - V1 (`FullVehicleResponseBody` from `@dms/api/sales`): `type` at the top level
 *    (typed as `VehicleTypeEnum`) and `fuelType` / `hybridType` nested inside
 *    `vehicle`.
 *  - V2 (`GetVehicleApiResponse` from `@dms/api/vehicle`): `type`, `fuelType`,
 *    and `hybridType` all at the top level (typed loosely as `string | null`).
 *
 * The helper reads from the top level first and falls back to the nested `vehicle`
 * object if the top-level value is missing, covering both shapes transparently.
 *
 * String values for all three fields are validated against the corresponding
 * Zod enum schemas; values outside the enum are dropped (returned as `undefined`).
 */
export interface VehicleFieldAvailabilitySource {
  type?: VehicleTypeEnum | string | null;
  fuelType?: string | null;
  hybridType?: string | null;
  vehicle?: {
    fuelType?: string | null;
    hybridType?: string | null;
  } | null;
}

/**
 * Derives a {@link FieldAvailabilityContext} from a vehicle response.
 *
 * Used to plumb the current vehicle's `vehicleType`, `fuelType`, and `hybridType`
 * into `isMandatoryFieldsEnabled` / `getMandatoryFields` so context-gated mandatory
 * fields (e.g. PHEV-only kWh consumption fields) are correctly evaluated when
 * deciding whether the FE should request server-side mandatory-field validation.
 *
 * @example
 * validateMandatoryFields: isMandatoryFieldsEnabled(
 *   condition,
 *   getFieldAvailabilityContextFromVehicle(vehicleData)
 * )
 */
export const getFieldAvailabilityContextFromVehicle = (
  vehicleData: VehicleFieldAvailabilitySource | null | undefined
): FieldAvailabilityContext => {
  const rawFuelType = vehicleData?.fuelType ?? vehicleData?.vehicle?.fuelType;
  const rawHybridType = vehicleData?.hybridType ?? vehicleData?.vehicle?.hybridType;

  const vehicleTypeResult = VehicleTypeEnumSchema.safeParse(vehicleData?.type);
  const fuelTypeResult = FuelTypeEnumSchema.safeParse(rawFuelType);
  const hybridTypeResult = HybridTypeEnumSchema.safeParse(rawHybridType);

  return {
    vehicleType: vehicleTypeResult.success ? vehicleTypeResult.data : undefined,
    fuelType: fuelTypeResult.success ? fuelTypeResult.data : undefined,
    hybridType: hybridTypeResult.success ? hybridTypeResult.data : undefined,
  };
};
